import { StackScreenProps } from "@react-navigation/stack";
import {
  Card,
  FlatActionList,
  Page,
  Stack,
  Text,
  TextButton,

} from "@towersystems/roam-common-native";
import { Switch } from "react-native";
import { View } from "react-native-ui-lib";
import { SettingsRootParamList } from "../../../../types";
import {
  useGlobalSettingsProvider,
  useMutationUpdateGlobalSettings,
} from "../../../../utilities/global-settings/hooks";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { useAuthenticationProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";
import { AccountCountryType } from "@towersystems/roam-common/lib/generated-types";
import { useTranslation } from "react-i18next";

export interface GeneralSettingsScreenProps
  extends StackScreenProps<SettingsRootParamList, "GeneralSettingsScreen"> {}

export const GeneralSettingsScreen = ({
  navigation,
}: GeneralSettingsScreenProps) => {
  const { t } = useTranslation();
  const { user } = useAuthenticationProvider();
  const { setSetting, settings } = useLocalSettingsProvider();
  const globalSettings = useGlobalSettingsProvider();
  const { currentVendor } = useCurrentVendorProvider();
  const { automaticallyCompleteSale } = settings;
  const { updateGlobalSettings } = useMutationUpdateGlobalSettings();

  return (
    <Page title="General Settings">
      <Stack vertical>
        <Card title="Business Details">
          <FlatActionList
            items={[
              {
                key: "business_name",
                title: "Business Name",
                componentRight: () => {
                  return (
                    <View>
                      <Text>{globalSettings.businessName || "N/A"}</Text>
                    </View>
                  );
                },
              },
              {
                key: "abn",
                title:
                  currentVendor.countryType === AccountCountryType.AUSTRALIA
                    ? "ABN"
                    : "GST Number",

                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate("EditBusinessAbnScreen")
                      }
                    >
                      {globalSettings.abn ||
                        (currentVendor.countryType ===
                        AccountCountryType.AUSTRALIA
                          ? "Set ABN"
                          : "Set GST Number")}
                    </TextButton>
                  );
                },
              },
              {
                key: "website",
                title: "Website",
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate("EditBusinessWebsiteScreen")
                      }
                    >
                      {globalSettings.website || "Set website"}
                    </TextButton>
                  );
                },
              },
              {
                key: "email",
                title: "Email",
                componentRight: () => {
                  return (
                    <View>
                      <Text>{globalSettings.email || "-"}</Text>
                    </View>
                  );
                },
              },

              {
                key: "address",
                title: "Address",
                componentRight: () => {
                  return (
                    <View>
                      <Text>{globalSettings.address1 || "-"}</Text>
                      <Text>{globalSettings.address2 || ""}</Text>
                    </View>
                  );
                },
              },
              {
                key: "postcode",
                title: "Postcode",
                componentRight: () => {
                  return (
                    <View>
                      <Text>{globalSettings.postcode || "-"}</Text>
                    </View>
                  );
                },
              },

              {
                key: "phone",
                title: "Phone",
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate("EditBusinessPhoneScreen")
                      }
                    >
                      {globalSettings.phone || "Set phone number"}
                    </TextButton>
                  );
                },
              },

              {
                key: "country_type",
                title: "Country Type",
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate(
                          "SelectAccountCountryTypeScreenScreen",
                        )
                      }
                    >
                      {t(currentVendor.countryType) || "Set country type"}
                    </TextButton>
                  );
                },
              },
              {
                key: "timezone",
                title: "Timezone",
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() => navigation.navigate("SelectTimezoneScreen")}
                    >
                      {globalSettings.timezone || "Set timezone"}
                    </TextButton>
                  );
                },
              },
            ]}
          ></FlatActionList>
        </Card>

        <Card title="Checkout">
          <FlatActionList
            items={[
              {
                key: "automaticallyCompleteSale",
                title: "Automatically Go Next Sale",
                subTitle:
                  "Automatically go to next sale when no user interaction",
                componentRight: () => {
                  return (
                    <View>
                      <Switch
                        value={automaticallyCompleteSale}
                        onValueChange={() =>
                          setSetting(
                            "automaticallyCompleteSale",
                            !automaticallyCompleteSale,
                          )
                        }
                      />
                    </View>
                  );
                },
              },
              {
                key: "cashManagedPaymentMethodId",
                title: "Cash Managed Payment Method",
                subTitle: "Payment method used for cash managed sales",
                onPress: () =>
                  navigation.navigate("EditCashManagedPaymentMethodScreen"),
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate(
                          "EditCashManagedPaymentMethodScreen",
                        )
                      }
                      color={
                        globalSettings.cashManagedPaymentMethodId
                          ? "action"
                          : "warning"
                      }
                    >
                      {globalSettings.cashManagedPaymentMethodId
                        ? "Change Method"
                        : "Required!"}
                    </TextButton>
                  );
                },
              },
            ]}
          ></FlatActionList>
        </Card>

        <Card title="Security">
          <FlatActionList
            items={[
              {
                key: "mfaSettings",
                title: "Multi-Factor Authentication",
                subTitle:
                  "Secure your account with multi-factor authentication",
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate("SetupNative2faRootScreen", {
                          screen: "ListNative2faMethodsScreen",
                        })
                      }
                    >
                      Manage
                    </TextButton>
                  );
                },
              },
            ]}
          ></FlatActionList>
        </Card>

        <Card title="Data">
          <FlatActionList
            items={[
              {
                key: "retailerDataCoreEnabled",
                title: "Retailer Base Data",
                subTitle:
                  "Enable automatic product data lookup from Retailer base data when no product match is found locally",
                componentRight: () => {
                  return (
                    <View>
                      <Switch
                        value={globalSettings.retailerDataCoreEnabled}
                        onValueChange={() => {
                          updateGlobalSettings({
                            retailerDataCoreEnabled:
                              !globalSettings.retailerDataCoreEnabled,
                          }).catch((error) => {
                            console.error(
                              "Failed to update retailer data core setting",
                              error,
                            );
                          });
                        }}
                      />
                    </View>
                  );
                },
              },
            ]}
          ></FlatActionList>
        </Card>
      </Stack>
    </Page>
  );
};
